import { Badge, Icon, When, useToast } from '@/components';
import { cn } from '@/lib/cn';
import { ApiClientError, type WorkflowRunAttempt } from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';
import { RunFailurePanel } from './RunFailurePanel';
import { fetchRunLogsUrl } from './hooks';

/**
 * Attempt history of a workflow-run row (UCH-453): every terminal attempt with
 * its outcome, failure detail, and archived full-log download when available.
 */
export const RunAttemptsPanel = (props: {
  releaseId: string;
  runId: string;
  attempts: WorkflowRunAttempt[];
}) => {
  const toast = useToast();
  const [open, setOpen] = createSignal(false);
  const downloadLogs = async (attemptNumber: number) => {
    try {
      const url = await fetchRunLogsUrl(props.releaseId, props.runId, attemptNumber);
      window.open(url, '_blank', 'noreferrer');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.error.message : 'Request failed');
    }
  };
  return (
    <div class="mt-1">
      <button
        type="button"
        class="flex items-center gap-1 text-xs text-neutral-500 hover:underline dark:text-neutral-400"
        aria-expanded={open()}
        onClick={() => setOpen(!open())}
      >
        <Icon
          name="angle-small-down"
          class={cn(
            'shrink-0 transition-transform duration-200 motion-reduce:transition-none',
            !open() && '-rotate-90',
          )}
        />
        Attempts ({props.attempts.length})
      </button>
      <div
        inert={!open()}
        class={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          open() ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div class="overflow-hidden">
          <ul class="mt-2 flex flex-col gap-2">
            <For each={props.attempts}>
              {(attempt) => (
                <li class="rounded-lg border border-neutral-200 p-2 text-xs dark:border-neutral-800">
                  <div class="flex items-center justify-between gap-2">
                    <span class="font-medium">Attempt #{attempt.attemptNumber}</span>
                    <span class="flex shrink-0 items-center gap-2">
                      <When date={attempt.completedAt} />
                      <Badge tone={attempt.status === 'succeeded' ? 'success' : 'danger'}>
                        {attempt.status}
                      </Badge>
                      <Show when={attempt.htmlUrl}>
                        <a
                          href={attempt.htmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          class="text-brand-600 hover:underline dark:text-brand-400"
                        >
                          View on GitHub
                        </a>
                      </Show>
                      <Show when={attempt.logArchiveAvailable}>
                        <button
                          type="button"
                          class="text-brand-600 hover:underline dark:text-brand-400"
                          onClick={() => downloadLogs(attempt.attemptNumber)}
                        >
                          Download logs
                        </button>
                      </Show>
                    </span>
                  </div>
                  <Show when={attempt.failureDetail}>
                    {(detail) => <RunFailurePanel detail={detail()} />}
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </div>
      </div>
    </div>
  );
};
